// Admin authentication via Cognito (amazon-cognito-identity-js, no Amplify).
// The ID token is used as the bearer for REST calls and the WS admin handshake.

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { loadConfig } from '../config';

let poolPromise: Promise<CognitoUserPool> | null = null;

async function getPool(): Promise<CognitoUserPool> {
  if (!poolPromise) {
    poolPromise = loadConfig().then(
      (cfg) => new CognitoUserPool({ UserPoolId: cfg.userPoolId, ClientId: cfg.userPoolClientId })
    );
  }
  return poolPromise;
}

export type SignInResult =
  | { status: 'success'; idToken: string; email: string }
  | { status: 'newPasswordRequired'; complete: (newPassword: string) => Promise<SignInResult> };

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const pool = await getPool();
  const user = new CognitoUser({ Username: email, Pool: pool });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise<SignInResult>((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(sessionResult(session)),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        resolve({
          status: 'newPasswordRequired',
          complete: (newPassword: string) =>
            new Promise<SignInResult>((res, rej) => {
              user.completeNewPasswordChallenge(newPassword, {}, {
                onSuccess: (session) => res(sessionResult(session)),
                onFailure: (err) => rej(err),
              });
            }),
        });
      },
    });
  });
}

function sessionResult(session: CognitoUserSession): SignInResult {
  const idToken = session.getIdToken();
  return {
    status: 'success',
    idToken: idToken.getJwtToken(),
    email: (idToken.decodePayload() as { email?: string }).email ?? '',
  };
}

/** Returns a valid ID token for the current user, refreshing if needed, or null. */
export async function currentIdToken(): Promise<string | null> {
  const pool = await getPool();
  const user = pool.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export async function currentEmail(): Promise<string | null> {
  const token = await currentIdToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1])) as { email?: string };
    return payload.email ?? null;
  } catch {
    return null;
  }
}

export async function signOut(): Promise<void> {
  const pool = await getPool();
  pool.getCurrentUser()?.signOut();
}
